import cn from 'classnames';
import { FC, ReactNode, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Icon, Input } from '@/shared/ui';

import { PartnerTemplateBadge } from '@/entities/templates';

import type { CatalogOrigin, WorkspaceCatalogItem } from '../lib';

import styles from './WorkspaceTemplateCatalog.module.scss';

type Props = {
  items: WorkspaceCatalogItem[];
  selectedIds: string[];
  disabled: boolean;
  // How loading the items went, for the text of an empty list.
  status?: 'loading' | 'failed';
  // Replaces "Treatment template" above the search.
  title?: ReactNode;
  // Without it the list starts at its search, where tabs above name it.
  heading?: boolean;
  // A group heading folds its cards away; a search shows them all.
  collapsibleGroups?: boolean;
  // Groups that start folded; one holding the selected card stays open.
  foldedGroups?: string[];
  // Said by a list with nothing in it, in place of the language's text.
  emptyText?: ReactNode;
  // The catalog takes the height of its panel, and only its list scrolls.
  fill?: boolean;
  children?: ReactNode;
  className?: string;
  onSelect: (id: string) => void;
  onAdd?: (id: string) => void;
};

export const CatalogOriginLabel: FC<{ origin: CatalogOrigin }> = ({
  origin,
}) => {
  if (origin === 'clinic') {
    return (
      <FormattedMessage
        id="workspace.template.clinicSource"
        defaultMessage="Clinic template"
      />
    );
  }
  if (origin === 'official') {
    return (
      <FormattedMessage
        id="workspace.template.officialSource"
        defaultMessage="Official template"
      />
    );
  }
  if (origin === 'design') {
    return (
      <FormattedMessage
        id="workspace.template.designSource"
        defaultMessage="Design example"
      />
    );
  }
  if (origin === 'export') {
    return (
      <FormattedMessage
        id="workspace.template.exportSource"
        defaultMessage="Organization export"
      />
    );
  }
  return (
    <FormattedMessage
      id="workspace.template.communitySource"
      defaultMessage="Community template"
    />
  );
};

export const WorkspaceTemplateCatalog: FC<Props> = ({
  items,
  selectedIds,
  disabled,
  status,
  title,
  heading = true,
  collapsibleGroups = false,
  foldedGroups = [],
  emptyText,
  fill = false,
  children,
  className,
  onSelect,
  onAdd,
}) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  // The groups folded or opened by hand, against how they start.
  const [toggledGroups, setToggledGroups] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [showOutside, setShowOutside] = useState(false);
  const categories = [
    ...new Set(items.flatMap((item) => (item.category ? [item.category] : []))),
  ];
  const matching = items.filter(
    (item) =>
      (!category || item.category === category) &&
      `${item.group ?? ''} ${item.name} ${item.description ?? ''}`
        .toLocaleLowerCase()
        .includes(query.trim().toLocaleLowerCase()),
  );
  // Protocols of other treatment schemas cannot be recorded here; they stay
  // reachable through search or the toggle instead of crowding the list.
  const outsideCount = matching.filter(
    (item) => item.state === 'outside',
  ).length;
  const outsideVisible = showOutside || !!query.trim();
  const visible = outsideVisible
    ? matching
    : matching.filter((item) => item.state !== 'outside');

  // A failed load is told by the retry above the list.
  const renderEmpty = () => {
    if (items.length > 0) {
      return (
        <FormattedMessage
          id="workspace.template.noResults"
          defaultMessage="No templates match this search."
        />
      );
    }
    if (status === 'failed') {
      return null;
    }
    if (status === 'loading') {
      return <FormattedMessage id="global.loading" defaultMessage="Loading…" />;
    }
    return (
      emptyText ?? (
        <FormattedMessage
          id="workspace.template.libraryEmpty"
          defaultMessage="No treatment templates in this language."
        />
      )
    );
  };
  const empty = visible.length === 0 && outsideCount === 0 && renderEmpty();
  const searching = !!query.trim();
  const holdsSelected = (group: string) =>
    items.some((item) => item.group === group && selectedIds.includes(item.id));
  const isFolded = (group?: string) =>
    collapsibleGroups &&
    !searching &&
    !!group &&
    !holdsSelected(group) &&
    foldedGroups.includes(group) !== toggledGroups.includes(group);

  const toggleGroup = (group: string) =>
    setToggledGroups((current) =>
      current.includes(group)
        ? current.filter((toggled) => toggled !== group)
        : [...current, group],
    );

  const renderGroup = (group: string) =>
    collapsibleGroups ? (
      <button
        type="button"
        className={cn('p4', styles.group, styles.groupToggle)}
        data-component="workspace-template-catalog-group"
        aria-expanded={!isFolded(group)}
        disabled={disabled || searching || holdsSelected(group)}
        onClick={() => toggleGroup(group)}
      >
        {group}
        <Icon
          name="arrowDown"
          size={14}
          className={cn(
            styles.groupChevron,
            isFolded(group) && styles.groupFolded,
          )}
        />
      </button>
    ) : (
      <p
        className={cn('p4', styles.group)}
        data-component="workspace-template-catalog-group"
      >
        {group}
      </p>
    );
  // Stable, so the list scrolls to a card only when it becomes the selected
  // one, e.g. a template opened by a link, and not on every render.
  const revealSelected = useCallback((card: HTMLButtonElement | null) => {
    card?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  return (
    <div className={cn(styles.catalog, fill && styles.fill, className)}>
      {heading && (
        <div className={styles.heading}>
          <span className={cn('p4', styles.label)}>
            {title ??
              (selectedIds.length > 1 ? (
                <FormattedMessage
                  id="workspace.template.selectedCount"
                  defaultMessage="{count, plural, one {# treatment template} other {# treatment templates}}"
                  values={{ count: selectedIds.length }}
                />
              ) : (
                <FormattedMessage
                  id="workspace.template.catalogTitle"
                  defaultMessage="Treatment template"
                />
              ))}
          </span>
          <span className={cn('p4', styles.source)}>
            <FormattedMessage
              id="workspace.template.library"
              defaultMessage="Template library"
            />
          </span>
        </div>
      )}
      <Input
        name="workspace-template-search"
        icon="search"
        className={styles.searchBox}
        inputClassName={styles.search}
        disabled={disabled}
        aria-label={formatMessage({
          id: 'workspace.template.search',
          defaultMessage: 'Search templates…',
        })}
        placeholder={formatMessage({
          id: 'workspace.template.search',
          defaultMessage: 'Search templates…',
        })}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {categories.length > 0 && (
        <div className={styles.filters}>
          {['', ...categories].map((item) => (
            <button
              type="button"
              key={item || 'all'}
              className={cn('p3b', styles.filter)}
              aria-pressed={category === item}
              disabled={disabled}
              onClick={() => setCategory(item)}
            >
              {item || (
                <FormattedMessage id="global.all" defaultMessage="All" />
              )}
            </button>
          ))}
        </div>
      )}
      {children}
      <div className={styles.list}>
        {visible.map((item, index) => {
          const groupStarts =
            !!item.group && item.group !== visible[index - 1]?.group;
          const folded = isFolded(item.group);
          if (folded && !groupStarts) {
            return null;
          }
          return (
            <div className={styles.item} key={`${item.group ?? ''}/${item.id}`}>
              {groupStarts && item.group && renderGroup(item.group)}
              {!folded && (
                <button
                  ref={
                    selectedIds.includes(item.id) ? revealSelected : undefined
                  }
                  type="button"
                  className={styles.card}
                  aria-pressed={selectedIds.includes(item.id)}
                  disabled={disabled || item.state !== 'selectable'}
                  onClick={() => onSelect(item.id)}
                >
                  <span className={cn('p3b', styles.name)}>{item.name}</span>
                  {item.partner !== undefined && (
                    <PartnerTemplateBadge
                      partner={item.partner}
                      className={styles.badge}
                    />
                  )}
                  <span className={cn('p4', styles.meta)}>
                    {item.category && `${item.category} · `}
                    <CatalogOriginLabel origin={item.origin} /> ·{' '}
                    <FormattedMessage
                      id="workspace.template.sectionCount"
                      defaultMessage="{count, plural, one {# section} other {# sections}}"
                      values={{ count: item.sectionsCount }}
                    />
                  </span>
                  {item.state === 'outside' && (
                    <span className={cn('p4', styles.state)}>
                      <FormattedMessage
                        id="workspace.template.outsideSchema"
                        defaultMessage="Not in this treatment schema"
                      />
                    </span>
                  )}
                  {item.state === 'required' && (
                    <span className={cn('p4', styles.state)}>
                      <FormattedMessage
                        id="workspace.document.required"
                        defaultMessage="Required"
                      />
                    </span>
                  )}
                  {item.communityVersionId && (
                    <span
                      className={cn('p4', styles.state)}
                      data-component="workspace-template-catalog-community-version"
                    >
                      <FormattedMessage
                        id="workspace.templates.communityVersion.notice"
                        defaultMessage="A newer version is available in the community"
                      />
                    </span>
                  )}
                </button>
              )}
              {!folded &&
                onAdd &&
                selectedIds.length > 0 &&
                item.state === 'selectable' &&
                !selectedIds.includes(item.id) && (
                  <button
                    type="button"
                    className={cn('p4', styles.add)}
                    disabled={disabled}
                    aria-label={formatMessage(
                      {
                        id: 'workspace.template.addAdditionalNamed',
                        defaultMessage:
                          'Add “{name}” as an additional protocol',
                      },
                      { name: item.name },
                    )}
                    onClick={() => onAdd(item.id)}
                  >
                    <FormattedMessage
                      id="workspace.template.addAdditional"
                      defaultMessage="+ Add as additional protocol"
                    />
                  </button>
                )}
            </div>
          );
        })}
        {outsideCount > 0 && !query.trim() && (
          <button
            type="button"
            className={cn('p4', styles.outsideToggle)}
            disabled={disabled}
            aria-expanded={showOutside}
            onClick={() => setShowOutside((current) => !current)}
          >
            {showOutside ? (
              <FormattedMessage
                id="workspace.template.hideOutside"
                defaultMessage="Hide clinic templates outside this schema"
              />
            ) : (
              <FormattedMessage
                id="workspace.template.showOutside"
                defaultMessage="{count, plural, one {# more clinic template} other {# more clinic templates}} outside this schema"
                values={{ count: outsideCount }}
              />
            )}
          </button>
        )}
        {empty && <p className={cn('p3', styles.empty)}>{empty}</p>}
      </div>
    </div>
  );
};
